import { fieldsOf, flag, iconOf, identifier, isPlainRecord, listOf, listed, name, oneOf, pathTo, report, ticks, whole, type Place } from "./checks.ts";
import { cleansesOf } from "./cleanse-records.ts";
import { actionStrip, chainOf, comboOf, oneOfComboOrChain } from "./combo-records.ts";
import { onHitOf } from "./on-hit-records.ts";
import { coneAbilityChecks, MAX_IMPACT_TICKS } from "./impact-records.ts";
import { projectileAbilityChecks, projectileShapeOf } from "./projectile-records.ts";
import { drinkOf } from "./drink-records.ts";
import { restoreStaminaOf, shortensOf } from "./restore-records.ts";
import {
  BLOWS,
  MAX_EFFECT_TICKS,
  MAX_COOLDOWN_TICKS,
  MAX_HEAL_AMOUNT,
  DAMAGE_TYPES,
  RESOURCE_IDS,
  SHAPE_KINDS,
  type Ability,
  type AbilityEffect,
  type AbilityShape,
  type Cost,
  type Damage,
  type Displacement,
  type Heal,
  type Restore,
  type Shorten,
  type Weapon,
  type WeaponBlow,
} from "./types.ts";

/**
 * Each table's record checks (ADR-069 decisions 3 and 5): one reader per table, which checks a record's fields
 * against its shape, reports every problem at the record's place, and builds the record of the declared type
 * from the checked fields, or gives null. The validator runs them in turn and adds the checks across a table.
 */

const ABILITY_FIELDS = ["id", "name", "strip", "cooldownTicks", "costs", "shape", "damage", "effects", "given", "displacement", "onHit", "cleanses", "combo", "chain", "heal", "restoreStamina", "shortens", "drink", "icon"] as const;
const SHAPE_FIELDS: Readonly<Record<AbilityShape["kind"], readonly string[]>> = {
  cone: ["kind", "radiusSixteenths", "degrees", "maxTargets", "impactTicks"],
  line: ["kind", "lengthSixteenths", "widthSixteenths", "maxTargets"],
  target: ["kind", "rangeSixteenths"],
  ally: ["kind", "rangeSixteenths"],
  self: ["kind"],
  projectile: ["kind", "rangeSixteenths", "minRangeSixteenths", "speedSixteenths", "radiusSixteenths", "maxTargets", "releaseTicks", "look"],
};
const FULL_TURN_DEGREES = 360;

/**
 * The most targets a cone or a line may strike: every target a combat frame's `u8` count can name.
 */
const MAX_TARGETS = 255;

/**
 * The most ticks a displacement may take.
 */
const MAX_DISPLACEMENT_TICKS = 255;

/**
 * Checks an ability's costs: each a resource at most once and an amount that is whole and never negative.
 *
 * @param place Where to report.
 * @param value The costs.
 * @returns The costs, or null.
 */
function costsOf(place: Place, value: unknown): Cost[] | null {
  const list = listOf(place, value, "costs");
  if (list === null) {
    return null;
  }
  const costs: Cost[] = [];
  let ok = true;
  for (const [index, item] of list.entries()) {
    const cost = fieldsOf(place, item, pathTo("costs", index, true), ["resource", "amount"], "a cost");
    const resource = cost?.["resource"];
    const amount = cost?.["amount"];
    if (cost === null || resource === undefined || amount === undefined) {
      ok = false;
    } else if (typeof resource !== "string" || !(RESOURCE_IDS as readonly string[]).includes(resource)) {
      report(place, `costs names ${String(resource)}, which is not a resource (${listed(RESOURCE_IDS)})`);
      ok = false;
    } else if (costs.some((known) => known.resource === resource)) {
      report(place, `costs names ${resource} twice`);
      ok = false;
    } else if (typeof amount === "number" && Number.isInteger(amount) && amount < 0) {
      report(place, `the ${resource} cost is ${amount}, and a cost is never negative`);
      ok = false;
    } else {
      const checked = whole(place, amount, `the ${resource} cost`, 0);
      if (checked === null || Object.keys(cost).length !== 2) {
        ok = false;
      } else {
        costs.push({ resource: resource as Cost["resource"], amount: checked });
      }
    }
  }
  return ok ? costs : null;
}

/**
 * Checks an ability's shape: a kind the game resolves, and exactly that kind's measures.
 *
 * @param place Where to report.
 * @param value The shape.
 * @returns The shape, or null.
 */
function shapeOf(place: Place, value: unknown): AbilityShape | null {
  if (value === undefined) {
    return null;
  }
  if (!isPlainRecord(value)) {
    report(place, "shape is not a record");
    return null;
  }
  if (value["kind"] === undefined) {
    report(place, "shape.kind is missing");
    return null;
  }
  const kind = oneOf(place, value["kind"], "shape.kind", SHAPE_KINDS, "a shape the game resolves");
  if (kind === null) {
    return null;
  }
  const fields = SHAPE_FIELDS[kind];
  const shape = fieldsOf(place, value, "shape", fields, kind === "ally" ? "an ally" : `a ${kind}`) as Readonly<Record<string, unknown>>;
  const exact = Object.keys(shape).length === fields.length;
  switch (kind) {
    case "cone": {
      const radius = whole(place, shape["radiusSixteenths"], "shape.radiusSixteenths", 1);
      const degrees = whole(place, shape["degrees"], "shape.degrees", 1, FULL_TURN_DEGREES);
      const maxTargets = whole(place, shape["maxTargets"], "shape.maxTargets", 1, MAX_TARGETS);
      const impactTicks = ticks(place, shape["impactTicks"], "shape.impactTicks", 0, MAX_IMPACT_TICKS);
      return radius === null || degrees === null || maxTargets === null || impactTicks === null || !exact ? null : { kind, radiusSixteenths: radius, degrees, maxTargets, impactTicks };
    }
    case "line": {
      const length = whole(place, shape["lengthSixteenths"], "shape.lengthSixteenths", 1);
      const width = whole(place, shape["widthSixteenths"], "shape.widthSixteenths", 1);
      const maxTargets = whole(place, shape["maxTargets"], "shape.maxTargets", 1, MAX_TARGETS);
      return length === null || width === null || maxTargets === null || !exact ? null : { kind, lengthSixteenths: length, widthSixteenths: width, maxTargets };
    }
    case "target":
    case "ally": {
      const range = whole(place, shape["rangeSixteenths"], "shape.rangeSixteenths", 1);
      return range === null || !exact ? null : { kind, rangeSixteenths: range };
    }
    case "self":
      return exact ? { kind } : null;
    case "projectile":
      return projectileShapeOf(place, shape, exact);
  }
}

/**
 * Checks an ability's heal (ADR-091 decision 2): null for none, or a whole amount from 1 to `MAX_HEAL_AMOUNT`, which only
 * a shape that lands on oneself or an ally may carry.
 *
 * @param place Where to report.
 * @param value The heal.
 * @param shape The ability's shape, null when it was wrong.
 * @returns The heal, null for none, or undefined when it is missing or wrong.
 */
function healOf(place: Place, value: unknown, shape: AbilityShape | null): Heal | null | undefined {
  if (value === null) {
    return null;
  }
  if (value === undefined) {
    return undefined;
  }
  if (!isPlainRecord(value)) {
    report(place, "heal is not a record");
    return undefined;
  }
  const fields = fieldsOf(place, value, "heal", ["amount"], "a heal") as Readonly<Record<string, unknown>>;
  const amount = whole(place, fields["amount"], "heal.amount", 1, MAX_HEAL_AMOUNT);
  if (shape !== null && shape.kind !== "ally" && shape.kind !== "self") {
    report(place, `a heal lands on oneself or an ally, and this shape is a ${shape.kind}`);
    return undefined;
  }
  return amount === null || Object.keys(fields).length !== 1 ? undefined : { amount };
}

/**
 * What a helping ability holds, read for `onlyHelps`: its damage (null for none, undefined when it was wrong), the
 * effects it lands, its chances on a hit, its cleanses and its shortens (each null when they were wrong), its combo
 * (undefined when it was wrong), and its heal and stamina restore (each undefined when it was wrong).
 */
interface Helping {
  readonly damage: Damage | null | undefined;
  readonly effects: readonly string[] | null;
  readonly onHit: readonly unknown[] | null;
  readonly cleanses: readonly string[] | null;
  readonly combo: unknown;
  readonly heal: Heal | null | undefined;
  readonly restoreStamina: Restore | null | undefined;
  readonly shortens: readonly Shorten[] | null;
}

/**
 * Holds an ability whose shape aims at oneself or an ally (ADR-079's amendment of 2026-10-03, item 1), or at oneself
 * alone (ADR-091 decision 2), to helping: no damage, no effects landed, no chance on a hit, and at least one thing done,
 * a heal, a stamina restore, an effect cleansed or an effect shortened (ADR-091's note for P2.8-T57), so nothing
 * harmful reaches a friend; a self shape also plays no combo.
 *
 * @param place Where to report.
 * @param kind The shape's kind.
 * @param helping What the ability holds.
 * @returns True when it only helps.
 */
function onlyHelps(place: Place, kind: "ally" | "self", helping: Helping): boolean {
  const shape = kind === "ally" ? "an ally shape" : "a self shape";
  let ok = true;
  if (helping.damage !== null && helping.damage !== undefined) {
    report(place, `${shape} deals no damage`);
    ok = false;
  }
  if (helping.effects !== null && helping.effects.length > 0) {
    report(place, `${shape} lands no effects`);
    ok = false;
  }
  if (helping.onHit !== null && helping.onHit.length > 0) {
    report(place, `${shape} rolls no chance on a hit`);
    ok = false;
  }
  if (helping.cleanses !== null && helping.cleanses.length === 0 && helping.heal === null && helping.restoreStamina === null && helping.shortens !== null && helping.shortens.length === 0) {
    report(place, `${shape} heals, restores stamina, cleanses or shortens at least one thing`);
    ok = false;
  }
  if (kind === "self" && helping.combo !== null && helping.combo !== undefined) {
    report(place, `${shape} plays no combo`);
    ok = false;
  }
  return ok;
}

/**
 * Checks an ability's damage: null for none, or an amount of at least 1 and a damage type.
 *
 * @param place Where to report.
 * @param value The damage.
 * @returns The damage, null for none, or undefined when it is missing or wrong.
 */
function damageOf(place: Place, value: unknown): Damage | null | undefined {
  if (value === null) {
    return null;
  }
  if (value !== undefined && !isPlainRecord(value)) {
    report(place, "damage is neither null nor a record");
    return undefined;
  }
  const damage = fieldsOf(place, value, "damage", ["amount", "type"], "damage");
  if (damage === null) {
    return undefined;
  }
  const amount = whole(place, damage["amount"], "damage.amount", 1);
  const type = oneOf(place, damage["type"], "damage.type", DAMAGE_TYPES, "a damage type");
  return amount === null || type === null || Object.keys(damage).length !== 2 ? undefined : { amount, type };
}

/**
 * Checks an ability's displacement: null for none, or the ticks it takes, 1 to 255, which only a line may carry.
 *
 * @param place Where to report.
 * @param value The displacement.
 * @param shape The ability's shape, null when it was wrong.
 * @returns The displacement, null for none, or undefined when it is missing or wrong.
 */
function displacementOf(place: Place, value: unknown, shape: AbilityShape | null): Displacement | null | undefined {
  if (value === null) {
    return null;
  }
  const fields = fieldsOf(place, value, "displacement", ["ticks"], "a displacement");
  if (fields === null) {
    return undefined;
  }
  const count = ticks(place, fields["ticks"], "displacement.ticks", 1, MAX_DISPLACEMENT_TICKS);
  if (shape !== null && shape.kind !== "line") {
    report(place, `displacement moves along a line, and this shape is a ${shape.kind}`);
    return undefined;
  }
  return count === null || Object.keys(fields).length !== 1 ? undefined : { ticks: count };
}

/**
 * The effects an ability applies: their ids and the entries as written (an id, or an id with a duration for this ability).
 */
interface AppliedEffects {
  readonly ids: string[];
  readonly entries: AbilityEffect[];
}

/**
 * Checks the effects an ability applies (ADR-079's amendment of 2026-10-21): each an id the effects table holds or a
 * record `{ effect, durationTicks }` naming how long the effect lasts for this ability, a whole number from 1 to the
 * longest an effect lasts (the `u16` its time left travels in), at most once an effect.
 *
 * @param place Where to report.
 * @param value The entries.
 * @param effectIds The ids the effects table holds.
 * @returns The ids and their durations, or null.
 */
function appliedEffects(place: Place, value: unknown, effectIds: ReadonlySet<string>): AppliedEffects | null {
  const list = listOf(place, value, "effects");
  if (list === null) {
    return null;
  }
  let ok = true;
  const applied: AppliedEffects = { ids: [], entries: [] };
  for (const [index, item] of list.entries()) {
    const path = pathTo("effects", index, true);
    let id: unknown = item;
    let duration: number | null = null;
    if (isPlainRecord(item)) {
      const fields = fieldsOf(place, item, path, ["effect", "durationTicks"], "an effect entry");
      id = fields?.["effect"];
      duration = ticks(place, fields?.["durationTicks"], `${path}.durationTicks`, 1, MAX_EFFECT_TICKS);
      if (fields === null || fields["effect"] === undefined || duration === null) {
        ok = false;
        continue;
      }
    }
    if (typeof id !== "string") {
      report(place, `${path} is not a string or a record naming an effect`);
      ok = false;
    } else if (applied.ids.includes(id)) {
      report(place, `effects names ${id} twice`);
      ok = false;
    } else if (!effectIds.has(id)) {
      report(place, `applies the effect ${id}, which the effects table does not hold`);
      ok = false;
    } else {
      applied.ids.push(id);
      applied.entries.push(duration === null ? id : { effect: id, durationTicks: duration });
    }
  }
  return ok ? applied : null;
}

/**
 * Checks an ability.
 *
 * @param place Where to report.
 * @param record The record.
 * @param effectIds The ids the effects table holds.
 * @returns The ability, or null.
 */
export function abilityOf(place: Place, record: Readonly<Record<string, unknown>>, effectIds: ReadonlySet<string>): Ability | null {
  const fields = fieldsOf(place, record, "", ABILITY_FIELDS, "an ability") as Readonly<Record<string, unknown>>;
  const id = identifier(place, fields["id"], "id");
  const abilityName = name(place, fields["name"], "name");
  const strip = actionStrip(place, fields["strip"], "strip");
  const cooldownTicks = ticks(place, fields["cooldownTicks"], "cooldownTicks", 0, MAX_COOLDOWN_TICKS);
  const costs = costsOf(place, fields["costs"]);
  const shape = shapeOf(place, fields["shape"]);
  const damage = damageOf(place, fields["damage"]);
  const applied = appliedEffects(place, fields["effects"], effectIds);
  const effects = applied?.ids ?? null;
  const entries = applied?.entries ?? null;
  const given = flag(place, fields["given"], "given");
  const displacement = fields["displacement"] === undefined ? undefined : displacementOf(place, fields["displacement"], shape);
  const onHit = onHitOf(place, fields["onHit"], effectIds, effects);
  const cleanses = cleansesOf(place, fields["cleanses"], effectIds, effects, onHit);
  const combo = fields["combo"] === undefined ? undefined : comboOf(place, fields["combo"], strip);
  const chain = fields["chain"] === undefined ? undefined : chainOf(place, fields["chain"], { strip, shape, damage, cooldownTicks });
  const single = oneOfComboOrChain(place, combo, chain);
  const heal = healOf(place, fields["heal"], shape);
  const restoreStamina = restoreStaminaOf(place, fields["restoreStamina"], shape);
  const shortens = shortensOf(place, fields["shortens"], effectIds, cleanses, shape);
  const drink = drinkOf(place, fields["drink"], shape, strip);
  const icon = iconOf(place, fields["icon"]);
  const helps = (shape?.kind !== "ally" && shape?.kind !== "self") || onlyHelps(place, shape.kind, { damage, effects, onHit, cleanses, combo, heal, restoreStamina, shortens });
  const thrown = shape?.kind !== "projectile" || projectileAbilityChecks(place, shape, { cooldownTicks, combo, given });
  const struck = shape?.kind !== "cone" || coneAbilityChecks(place, shape, { cooldownTicks, combo, chain });
  if (!helps || !thrown || !struck || id === null || abilityName === null || strip === null || cooldownTicks === null || costs === null || shape === null || damage === undefined || entries === null || given === null || displacement === undefined || onHit === null || cleanses === null || combo === undefined || chain === undefined || heal === undefined || restoreStamina === undefined || shortens === null || drink === undefined || icon === undefined || !single || Object.keys(fields).length !== ABILITY_FIELDS.length) {
    return null;
  }
  return { id, name: abilityName, strip, cooldownTicks, costs, shape, damage, effects: entries, given, displacement, onHit, cleanses, combo, chain, heal, restoreStamina, shortens, drink, icon };
}

/**
 * Checks a weapon's blows: each a blow at most once naming an ability the abilities table holds and every character
 * is not given, `basic` always.
 *
 * @param place Where to report.
 * @param value The blows.
 * @param abilityIds The ids the abilities table holds.
 * @param givenIds The ids of the abilities every character is given.
 * @returns The blows, or null.
 */
function blowsOf(place: Place, value: unknown, abilityIds: ReadonlySet<string>, givenIds: ReadonlySet<string>): WeaponBlow[] | null {
  const list = listOf(place, value, "blows");
  if (list === null) {
    return null;
  }
  let ok = true;
  const blows: WeaponBlow[] = [];
  for (const [index, item] of list.entries()) {
    const entry = fieldsOf(place, item, pathTo("blows", index, true), ["blow", "ability"], "a blow");
    const blow = entry?.["blow"];
    const ability = entry?.["ability"];
    if (entry === null || blow === undefined || ability === undefined) {
      ok = false;
    } else if (typeof blow !== "string" || !(BLOWS as readonly string[]).includes(blow)) {
      report(place, `blows names ${String(blow)}, which is not a blow (${listed(BLOWS)})`);
      ok = false;
    } else if (blows.some((known) => known.blow === blow)) {
      report(place, `blows names ${blow} twice`);
      ok = false;
    } else if (typeof ability !== "string" || !abilityIds.has(ability)) {
      report(place, `the ${blow} blow names the ability ${String(ability)}, which the abilities table does not hold`);
      ok = false;
    } else if (givenIds.has(ability)) {
      report(place, `the ${blow} blow names ${ability}, which every character is given`);
      ok = false;
    } else if (Object.keys(entry).length !== 2) {
      ok = false;
    } else {
      blows.push({ blow: blow as WeaponBlow["blow"], ability });
    }
  }
  if (!list.some((item) => isPlainRecord(item) && item["blow"] === "basic")) {
    report(place, "has no basic blow; every weapon has one");
    ok = false;
  }
  return ok ? blows : null;
}

/**
 * Checks a weapon.
 *
 * @param place Where to report.
 * @param record The record.
 * @param abilityIds The ids the abilities table holds.
 * @param givenIds The ids of the abilities every character is given, which no weapon may name.
 * @returns The weapon, or null.
 */
export function weaponOf(place: Place, record: Readonly<Record<string, unknown>>, abilityIds: ReadonlySet<string>, givenIds: ReadonlySet<string>): Weapon | null {
  const fields = fieldsOf(place, record, "", ["id", "name", "starting", "blows"], "a weapon") as Readonly<Record<string, unknown>>;
  const id = identifier(place, fields["id"], "id");
  const weaponName = name(place, fields["name"], "name");
  const starting = flag(place, fields["starting"], "starting");
  const blows = blowsOf(place, fields["blows"], abilityIds, givenIds);
  return id === null || weaponName === null || starting === null || blows === null || Object.keys(fields).length !== 4 ? null : { id, name: weaponName, starting, blows };
}
